import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';
import Modal from '../../components/Modal';

const MedicalAdminAssignBatchToDoctors = () => {
  const { user } = useAuth();
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [assignedDoctors, setAssignedDoctors] = useState([]);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [doctorSearchTerm, setDoctorSearchTerm] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [doctorToRemove, setDoctorToRemove] = useState(null);
  const [textReportLoading, setTextReportLoading] = useState(false);

  useEffect(() => {
    if (batchId) {
      loadBatchDetails();
      loadDoctors();
      loadAssignedDoctors();
    }
  }, [batchId]);

  const loadBatchDetails = async () => {
    try {
      const response = await api.get(`/batches/${batchId}`);
      setBatch(response.data.batch);
    } catch (err) {
      console.error('Error loading batch:', err);
      setError('Failed to load batch details');
    }
  };

  const loadDoctors = async () => {
    try {
      const response = await api.get('/doctors');
      const activeDoctors = (response.data.doctors || []).filter(
        doctor => doctor.is_active !== false && !doctor.email.startsWith('deleted_')
      );
      setDoctors(activeDoctors);
    } catch (err) {
      console.error('Error loading doctors:', err);
      setError('Failed to load doctors');
    }
  };

  const loadAssignedDoctors = async () => {
    try {
      const response = await api.get(`/batches/${batchId}`);
      setAssignedDoctors(response.data.assignments || []);
    } catch (err) {
      console.error('Error loading assigned doctors:', err);
    }
  };

  const handleDoctorToggle = (doctorId) => {
    setSelectedDoctors(prev => {
      if (prev.includes(doctorId)) {
        return prev.filter(id => id !== doctorId);
      } else {
        return [...prev, doctorId];
      }
    });
  };

  const handleAssignDoctors = async () => {
    if (selectedDoctors.length === 0) {
      setError('Please select at least one doctor to assign');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await api.post(`/batches/${batchId}/doctors`, {
        doctor_ids: selectedDoctors
      });

      const { assigned_doctors, skipped_doctors } = response.data;

      let message = `Successfully assigned batch to ${assigned_doctors.length} doctor(s).`;
      if (skipped_doctors.length > 0) {
        message += ` Skipped: ${skipped_doctors.join(', ')}`;
      }

      setSuccess(message);
      setSelectedDoctors([]);
      await loadAssignedDoctors();

      setTimeout(() => setSuccess(''), 5000);

    } catch (err) {
      console.error('Error assigning doctors:', err);
      setError(err.response?.data?.error || 'Failed to assign doctors to batch');
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  const handleRemoveDoctor = (doctorId) => {
    const doctor = assignedDoctors.find(a => a.doctor_id === doctorId);
    setDoctorToRemove(doctor);
    setShowRemoveModal(true);
  };

  const confirmRemoveDoctor = async () => {
    if (!doctorToRemove) return;

    try {
      setLoading(true);
      await api.delete(`/batches/${batchId}/doctors/${doctorToRemove.doctor_id}`);
      
      setSuccess('Doctor removed from batch successfully');
      await loadAssignedDoctors();
      
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('Error removing doctor:', err);
      setError(err.response?.data?.error || 'Failed to remove doctor from batch');
    } finally {
      setLoading(false);
      setShowRemoveModal(false);
      setDoctorToRemove(null);
    }
  };

  const openTextReport = async () => {
    try {
      setTextReportLoading(true);
      const response = await api.get(`/batches/report/text/all`, {
        responseType: 'text'
      });
      
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write('<pre>' + response.data + '</pre>');
        newWindow.document.close();
      }
    } catch (err) {
      console.error('Error loading text report:', err);
      setError('Failed to load text report');
    } finally {
      setTextReportLoading(false);
    }
  };

  const isDoctorAssigned = (doctorId) => {
    return assignedDoctors.some(assignment => assignment.doctor_id === doctorId);
  };

  const isDoctorSelected = (doctorId) => {
    return selectedDoctors.includes(doctorId);
  };

  const getBatchStatus = () => {
    if (!batch) return { text: 'Unknown', class: 'bg-secondary' };
    
    if (batch.is_expired) {
      return { text: 'Expired', class: 'bg-danger' };
    } else if (batch.is_active) {
      return { text: 'Active', class: 'bg-success' };
    } else {
      return { text: 'Scheduled', class: 'bg-warning' };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (!batch) {
    return (
      <ResearchLayout pageTitle="Assign Batch to Doctors">
        <div className="container-fluid">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading batch details...</p>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  const status = getBatchStatus();
  const availableDoctors = doctors.filter(doctor => !isDoctorAssigned(doctor.id));
  
  // Filter available doctors based on search term
  const filteredAvailableDoctors = availableDoctors.filter(doctor => {
    if (!doctorSearchTerm) return true;
    const searchLower = doctorSearchTerm.toLowerCase();
    return (
      doctor.name.toLowerCase().includes(searchLower) ||
      doctor.email.toLowerCase().includes(searchLower) ||
      String(doctor.id).includes(doctorSearchTerm)
    );
  });

  return (
    <ResearchLayout pageTitle={`Assign Batch to Doctors - ${batch.name}`}>
      <div className="container-fluid">
        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {success}
            <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
          </div>
        )}
        
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button type="button" className="btn-close" onClick={() => setError('')}></button>
          </div>
        )}

        {/* Batch Information Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-primary text-white">
                <div className="row align-items-center">
                  <div className="col">
                    <h4 className="mb-1">
                      <i className="fas fa-layer-group me-2"></i>
                      {batch.name}
                    </h4>
                    <small className="opacity-75">
                      {batch.description || 'No description provided'}
                    </small>
                  </div>
                  <div className="col-auto">
                    <span className={`badge ${status.class} fs-6 me-2`}>
                      {status.text}
                    </span>
                    <Button 
                      variant="light" 
                      size="sm" 
                      onClick={openTextReport}
                      disabled={textReportLoading}
                    >
                      <i className="fas fa-file-alt me-1"></i>
                      {textReportLoading ? 'Loading...' : 'Text Report'}
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3 mb-3">
                    <div className="small text-muted">Start Date</div>
                    <div className="fw-bold">
                      <i className="fas fa-play me-1 text-success"></i>
                      {formatDate(batch.start_date)}
                    </div>
                  </div>
                  
                  <div className="col-md-3 mb-3">
                    <div className="small text-muted">End Date</div>
                    <div className="fw-bold">
                      <i className="fas fa-stop me-1 text-danger"></i>
                      {formatDate(batch.extended_end_date || batch.end_date)}
                      {batch.extended_end_date && (
                        <small className="d-block text-success">
                          <i className="fas fa-clock"></i> Extended
                        </small>
                      )}
                    </div>
                  </div>
                  
                  <div className="col-md-3 mb-3">
                    <div className="small text-muted">Time Remaining</div>
                    <div className={`fw-bold ${batch.is_expired ? 'text-danger' : batch.is_active ? 'text-success' : 'text-warning'}`}>
                      <i className="fas fa-hourglass-half me-1"></i>
                      {batch.time_remaining || 'Expired'}
                    </div>
                  </div>
                  
                  <div className="col-md-3 mb-3">
                    <div className="small text-muted">Grace Period</div>
                    <div className="fw-bold">
                      <i className="fas fa-shield-alt me-1 text-info"></i>
                      {batch.grace_period_hours}h
                    </div>
                  </div>
                </div>
                
                <div className="row">
                  <div className="col-md-4 mb-3">
                    <div className="small text-muted">Cases in Batch</div>
                    <div className="h5 mb-0 text-primary">
                      <i className="fas fa-file-medical me-1"></i>
                      {batch.case_count || 0} Cases
                    </div>
                  </div>
                  
                  <div className="col-md-4 mb-3">
                    <div className="small text-muted">Currently Assigned</div>
                    <div className="h5 mb-0 text-info">
                      <i className="fas fa-user-md me-1"></i>
                      {assignedDoctors.length} Doctor{assignedDoctors.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  
                  <div className="col-md-4 mb-3">
                    <div className="small text-muted">Available Doctors</div>
                    <div className="h5 mb-0 text-success">
                      <i className="fas fa-users me-1"></i>
                      {availableDoctors.length} Available
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Assign New Doctors */}
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-user-plus text-primary me-2"></i>
                  Assign Doctors to Batch
                </h5>
                {selectedDoctors.length > 0 && (
                  <button 
                    className="btn btn-success btn-sm"
                    onClick={() => setShowConfirmModal(true)}
                    disabled={loading}
                  >
                    <i className="fas fa-user-plus me-1"></i>
                    Assign {selectedDoctors.length} Doctor{selectedDoctors.length !== 1 ? 's' : ''}
                  </button>
                )}
              </div>
              
              <div className="card-body">
                {/* Doctor Search */}
                <div className="mb-3">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="fas fa-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search doctors by name, email, or ID..."
                      value={doctorSearchTerm}
                      onChange={(e) => setDoctorSearchTerm(e.target.value)}
                    />
                    {doctorSearchTerm && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setDoctorSearchTerm('')}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                  {doctorSearchTerm && (
                    <div className="small text-muted mt-1">
                      <i className="fas fa-filter me-1"></i>
                      Showing {filteredAvailableDoctors.length} of {availableDoctors.length} available doctors
                    </div>
                  )}
                </div>

                {filteredAvailableDoctors.length > 0 ? (
                  <div className="row">
                    {filteredAvailableDoctors.map((doctor) => (
                      <div key={doctor.id} className="col-md-6 mb-3">
                        <div className={`card border-2 h-100 ${isDoctorSelected(doctor.id) ? 'border-primary bg-light' : 'border'}`}>
                          <div className="card-body p-3">
                            <div className="d-flex align-items-center">
                              <input
                                type="checkbox"
                                className="form-check-input me-3"
                                checked={isDoctorSelected(doctor.id)}
                                onChange={() => handleDoctorToggle(doctor.id)}
                                id={`doctor-${doctor.id}`}
                              />
                              <div className="flex-grow-1">
                                <label
                                  htmlFor={`doctor-${doctor.id}`}
                                  className="form-check-label w-100 cursor-pointer"
                                >
                                  <div className="fw-bold">{doctor.name}</div>
                                  <div className="small text-muted">
                                    <i className="fas fa-envelope me-1"></i>
                                    {doctor.email}
                                  </div>
                                  <div className="small text-muted">
                                    <i className="fas fa-id-badge me-1"></i>
                                    ID: {doctor.id}
                                  </div>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : availableDoctors.length > 0 ? (
                  <div className="text-center py-4">
                    <i className="fas fa-search fa-3x text-muted mb-3"></i>
                    <h6 className="text-muted">No doctors match your search</h6>
                    <p className="text-muted small">
                      Try adjusting your search terms to find doctors.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-user-check fa-3x text-success mb-3"></i>
                    <h6 className="text-success">All Available Doctors Assigned!</h6>
                    <p className="text-muted">
                      All active doctors have been assigned to this batch.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Currently Assigned Doctors */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h5 className="mb-0">
                  <i className="fas fa-users text-info me-2"></i>
                  Assigned Doctors ({assignedDoctors.length})
                </h5>
              </div>
              
              <div className="card-body">
                {assignedDoctors.length > 0 ? (
                  <div className="list-group list-group-flush">
                    {assignedDoctors.map((assignment) => (
                      <div key={assignment.id} className="list-group-item px-0 border-0">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="flex-grow-1">
                            <div className="fw-bold">{assignment.doctor_name}</div>
                            <div className="small text-muted">
                              Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}
                            </div>
                            <div className="small">
                              <span className={`badge ${
                                assignment.status === 'active' ? 'bg-success' : 'bg-secondary'
                              }`}>
                                {assignment.status}
                              </span>
                            </div>
                          </div>
                          
                          <div className="ms-2 d-flex">
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              onClick={() => navigate(`/medical-admin/batch/${batchId}/doctor/${assignment.doctor_id}/report`)}
                              title="View Doctor Progress Report"
                            >
                              <i className="fas fa-chart-bar"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveDoctor(assignment.doctor_id)}
                              disabled={loading}
                              title="Remove from batch"
                            >
                              <i className="fas fa-user-minus"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-user-friends fa-3x text-muted mb-3"></i>
                    <h6 className="text-muted">No Doctors Assigned</h6>
                    <p className="text-muted small">
                      Assign doctors to this batch to make cases available to them.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        <Modal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          title="Confirm Doctor Assignment"
          confirmText="Assign Doctors"
          onConfirm={handleAssignDoctors}
          isLoading={loading}
        >
          <p>Are you sure you want to assign the following {selectedDoctors.length} doctor{selectedDoctors.length !== 1 ? 's' : ''} to this batch?</p>
          
          <div className="mb-3">
            {selectedDoctors.map(doctorId => {
              const doctor = doctors.find(d => d.id === doctorId);
              return (
                <div key={doctorId} className="d-flex align-items-center mb-2">
                  <i className="fas fa-user-md text-primary me-2"></i>
                  <span className="fw-bold">{doctor?.name || `Doctor ${doctorId}`}</span>
                </div>
              );
            })}
          </div>
          
          <div className="alert alert-info">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Note:</strong> These doctors will be able to access and work on cases in this batch according to the batch schedule and deadlines.
          </div>
        </Modal>

        {/* Remove Doctor Confirmation Modal */}
        <Modal
          isOpen={showRemoveModal}
          onClose={() => setShowRemoveModal(false)}
          title="Remove Doctor from Batch"
          confirmText="Remove Doctor"
          onConfirm={confirmRemoveDoctor}
          isLoading={loading}
        >
          <p>Are you sure you want to remove <strong>{doctorToRemove?.doctor_name}</strong> from this batch?</p>
          
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Warning:</strong> This action will remove the doctor's access to cases in this batch. Their previously completed work will be preserved.
          </div>
        </Modal>
      </div>
    </ResearchLayout>
  );
};

export default MedicalAdminAssignBatchToDoctors;